function generateYearArray(maxYear, minYear) {
    if (minYear > maxYear) {
        throw new Error('Min year must be less than max year')
    }

    let year = maxYear
    const years = []
    while (year >= minYear) {
        years.push(year)
        year -= 1
    }

    return years
}

const minBatTrackingDate = '2023-07-14'

// This will potentially be out of date until the next time the pod restarts after the new year, hence "ish"
const currentYearIsh = new Date().getFullYear()

const minStatcastYear = 2015
const minPitchFxYear = 2008

const thresholds = {
    // We'll use actual months rather than JS zero indexed
    spring: [2, 16], // day after spring opener
    first: [3, 27], // day after MLB opener
    firstMinors: [3, 28], // day after AAA opener
    second: [3, 31], // 3-5 days after MLB opener
    third: [4, 10], // couple weeks for later stuff
    fourth: [5, 1], // real slow things
}

function getThresholdYear(type) {
    const [month, day] = thresholds[type]

    if (!month || !day) {
        throw new Error(`no threshold of type ${type}`)
    }

    const currentYear = new Date().getFullYear()
    const currentDate = new Date()
    const thresholdDate = new Date(currentYear, month - 1, day) // months are zero indexed

    const thresholdYear =
        thresholdDate < currentDate ? currentYear : currentYear - 1

    return thresholdYear
}

function getFirstThresholdDate() {
    const now = new Date()
    const currentYear = now.getFullYear()
    return new Date(currentYear, 2, 27) // March 27th
}

const appSettings = {
    // Home page
    SETTINGS_TOP_PERFORMERS: {
        get year() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    dragDashboard: {
        get year() {
            return getThresholdYear('third')
        },
    },
    // Batting
    batTracking: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            return generateYearArray(getThresholdYear('first'), 2023)
        },
        minBatTrackingDate,
    },
    battedBall: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    exitVelocity: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    expectedStats: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    homeRunTracking: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2019)
        },
    },
    percentileRankings: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    pitchArsenalStats: {
        get year() {
            return getThresholdYear('second')
        },
        min: 1,
        minPitches: 'q',
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2019)
        },
    },
    pitcherArmAngles: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2020)
        },
    },
    pitcherTempo: {
        baseState: 'Bases Empty',
        get year() {
            return getThresholdYear('second')
        },
        minPitches: 'q',
        isTake: 1,
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2010)
        },
    },
    swingTake: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minPitchFxYear)
        },
    },
    SETTINGS_YEAR_TO_YEAR: {
        get year() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, firstThresholdYear - 8)
        },
        get allYears() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
        useCache: true,
    },
    // Pitchers
    // Takes 100 pitches to show up, so don't have 2025 show up as an option early
    activeSpin: {
        get year() {
            return getThresholdYear('third')
        },
        useCache: true,
        get years() {
            const thirdThresholdYear = getThresholdYear('third')
            return generateYearArray(thirdThresholdYear, 2020)
        },
        min: 50,
    },
    pitchArsenals: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        spinCutoffYear: 2015,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minPitchFxYear)
        },
    },
    pitchMovement: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2017)
        },
    },
    // Handles comparison as well
    spinAxis: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2020)
        },
    },
    // FIELDING
    armStrength: {
        // Set because 1B is the longest to qualify @100 throws.
        minThrows: 50,
        get year() {
            return getThresholdYear('fourth')
        },
        useCache: true,
        // Start exposing the new year option later than on other leaderboards
        get years() {
            const thirdThresholdYear = getThresholdYear('third')
            return generateYearArray(thirdThresholdYear, 2020)
        },
    },
    catcherBlocking: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2018)
        },
    },
    catcherFraming: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    catcherFramingOld: {
        year: 2017,
        useCache: true,
        years: generateYearArray(2017, 2015),
    },
    catcherFramingNew: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2018)
        },
    },
    catcherThrowing: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    SETTINGS_CATCHER_STANCE: {
        firstDate: '2020-07-23',
        firstYear: 2020,
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2020)
        },
    },
    pitcherRunningGame: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    fieldingRuns: {
        get year() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
        minInnings: 100,
    },
    SETTINGS_FIELDING_RUN_VALUE: {
        get year() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2018)
        },
        firstYear: 2018,
        firstDate: '2018-03-29',
    },
    poptime: {
        get year() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
        useCache: true,
        min: 5,
    },
    catchProbability: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    directionalCatchProbability: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    infieldDefense: {
        get year() {
            return getThresholdYear('second')
        },
        min: 10,
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    jump: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    baseRunning: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    baseRunningRunValue: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    basestealingRunningGame: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    sprintSpeed: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
        min: 10,
    },
    runningSplits: {
        get year() {
            return getThresholdYear('fourth')
        },
        get playerYear() {
            return getThresholdYear('fourth')
        },
        useCache: true,
        min: 5,
        get years() {
            const thirdThresholdYear = getThresholdYear('third')
            return generateYearArray(thirdThresholdYear, minStatcastYear)
        },
    },
    SETTINGS_SLBI: {
        get year() {
            return getThresholdYear('first')
        },
        dateMin: `${currentYearIsh}-03-22`,
        dateMax: `${currentYearIsh}-11-01`,
    },
    // This is only powering an old URL that's kept around for scrapers
    oaa: {
        get year() {
            return getThresholdYear('first')
        },
        useCache: true,
    },
    parkFactors: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        firstWallChangeYear: 2022,
        secondWallChangeYear: 2025,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 1999)
        },
        get statcastyYears() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
        get dimensionYears() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2015)
        },
        earliestParkFactorYear: 1969,
        wallChangeDefault: 2024,
        wallChangeYears: generateYearArray(2024, 2022),
        get secondWallChangeYears() {
            const secondThresholdYear = getThresholdYear('second')
            return generateYearArray(secondThresholdYear, 2025)
        },
    },
    sortableStats: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 1950)
        },
    },
    // /statcast_hit_probability
    breakdown: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    pitchTimerInfractions: {
        get year() {
            return getThresholdYear('second')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2023)
        },
    },
    // /replay
    replay: {
        get year() {
            return getThresholdYear('first')
        },
        useCache: true,
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2014)
        },
    },
    // NON LEADERBOARD PLACES
    SETTINGS_STATISTICS_MENU: {
        get currentYear() {
            return getThresholdYear('first')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, firstThresholdYear - 5)
        },
        get statcastYears() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    SETTINGS_DERBY: {
        year: 2025,
        years: generateYearArray(2025, 2017),
    },
    draft: {
        year: 2025,
        years: generateYearArray(2025, 1965),
    },
    games: {
        get currentYear() {
            return getThresholdYear('fourth')
        },
    },
    illustrator: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const springThresholdYear = getThresholdYear('spring')
            return generateYearArray(springThresholdYear, minStatcastYear)
        },
    },
    league: {
        get year() {
            return getThresholdYear('first')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    team: {
        get year() {
            return getThresholdYear('first')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    playerScroll: {
        get year() {
            return getThresholdYear('second')
        },
    },
    // I feel like these should always have a year selected, but perhaps I'm wrong.
    searchCharts: {
        get year() {
            return getThresholdYear('first')
        },
    },
    SETTINGS_METRICS_CONTEXT: {
        get year() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
        minBatTrackingDate,
        minBatTrackingYear: 2023,
    },
    // Okay if this is slow, so an injured player could trend
    trending: {
        get year() {
            return getThresholdYear('second')
        },
    },
    weightedGameScore: {
        get year() {
            return getThresholdYear('first')
        },
    },

    // VISUALS imported
    brushing: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minPitchFxYear)
        },
    },
    SETTINGS_AFFINITY: {
        get currentYear() {
            return getThresholdYear('fourth')
        },
    },
    SETTINGS_HITTING_SIGNATURE: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minStatcastYear)
        },
    },
    SETTINGS_PLAYER_COMPARISON: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minPitchFxYear)
        },
    },
    // Flip When Schedule Comes Out
    map: {
        currentYear: 2025,
        years: generateYearArray(2026, 1974),
    },
    oaaVisual: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    pitchPercent: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, minPitchFxYear)
        },
    },
    pitchPlinko: {
        get currentYear() {
            return getThresholdYear('third')
        },
        get years() {
            const thirdThresholdYear = getThresholdYear('third')
            return generateYearArray(thirdThresholdYear, minPitchFxYear)
        },
    },
    positioning: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2016)
        },
    },
    swingMissProfile: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const secondThresholdYear = getThresholdYear('second')
            return generateYearArray(secondThresholdYear, minPitchFxYear)
        },
    },
    swingTakeVisual: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const secondThresholdYear = getThresholdYear('second')
            return generateYearArray(secondThresholdYear, minPitchFxYear)
        },
    },
    textureHeatMap: {
        get currentYear() {
            return getThresholdYear('second')
        },
        get years() {
            const secondThresholdYear = getThresholdYear('second')
            return generateYearArray(secondThresholdYear, minPitchFxYear)
        },
    },
    SETTINGS_HOT_STOVE: {
        currentYear: 2026,
        years: generateYearArray(2026, 2020),
        yearsOptions: generateYearArray(2026, 2020).map((d) => ({
            label: `${d - 1} - ${d}`,
            value: d,
        })),
        get statYears() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2019)
        },
        minPa: 100,
        minIp: 20,
        // Magic Numbers
        MAGIC: {
            unsigned: 99887766,
            sameTeam: 99998888,
            diffTeam: 99997777,
        },
    },
    // GAMES
    SETTINGS_SPRAY_CHART_GAME: {
        get year() {
            return getThresholdYear('fourth')
        },
    },
    // INTERNAL PAGES
    SETTINGS_PLAYER_RANKING: {
        lastTwoYears: generateYearArray(currentYearIsh, currentYearIsh - 1),
    },
    // PLAYER PAGE
    SETTINGS_PLAYER_PAGE: {
        get currentYear() {
            return getThresholdYear('first')
        },
    },
    SETTINGS_VENUE_INFO: {
        get currentYear() {
            return getThresholdYear('first')
        },
        get years() {
            const firstThresholdYear = getThresholdYear('first')
            return generateYearArray(firstThresholdYear, 2015)
        },
    },
    // CHANGELOG
    SETTINGS_CHANGELOG: {
        currentYear: currentYearIsh,
        years: generateYearArray(currentYearIsh, 2025),
    },
    // ABS
    SETTINGS_ABS: {
        get currentYear() {
            return getThresholdYear('spring')
        },
        get regSeasonFlipDate() {
            return getFirstThresholdDate()
        },
        firstMajorsYear: 2026,
        firstMinorsYear: 2025,
        // TODO: Change when threshold year flips
        get majorsYears() {
            const springThresholdYear = getThresholdYear('spring')
            return generateYearArray(springThresholdYear, 2025)
        },
        get minorsYears() {
            const springThresholdYear = getThresholdYear('spring')
            return generateYearArray(springThresholdYear, 2025)
        },
    },
    PIPELINE_PROSPECTS_CRON: {
        // Used for team history join
        currentYear: 2026,
    },
    SETTINGS_SEARCH: {
        get currentYear() {
            return getThresholdYear('spring')
        },
        get currentMinorsYear() {
            return getThresholdYear('firstMinors')
        },
        firstMinorsYear: 2021,
        firstStatcastYear: 2015,
        firstMajorsYear: 2008,
        wbcYears: [2026, 2023],
    },
}

module.exports = { appSettings, minPitchFxYear, minStatcastYear }
